/**
 * "Show on map" controls on the map page's route card: fuel on/off and which
 * stations, wind badges on/off, clearance warnings on/off, campgrounds on/off.
 *
 * Fuel and wind drive the planning screen's existing controls
 * (#showGasStations, .fuel-chain-check, #showWindIcons) by setting them and
 * firing their change events, so UIController's handlers stay the only place
 * that adds or removes those markers and both screens always agree.
 * Clearance warnings and campgrounds have no planning-screen control; hiding
 * them is a class on the map (.hide-clearance-markers,
 * .hide-campground-markers), so markers added later (another route, a
 * recalculation, the campground filters) follow it too.
 */
import { currentMarket } from '../services/market.js';

const $ = (id) => document.getElementById(id);

function fuelChecks() {
    return [...document.querySelectorAll('.fuel-chain-check')];
}

// The fuel options this market shows on the planning screen (US brands for
// US accounts, Australian ones for AU).
function availableFuelChecks() {
    const au = currentMarket() === 'AU';
    return fuelChecks().filter((cb) => !(cb.closest('.au-only') && !au) && !(cb.closest('.us-only') && au));
}

function fillFuelSelect() {
    const select = $('cardFuelFilter');
    if (!select) return;
    select.innerHTML = '';
    for (const cb of availableFuelChecks()) {
        const option = document.createElement('option');
        option.value = cb.value;
        option.textContent = cb.closest('label')?.textContent.trim().replace(/\s+/g, ' ') || cb.value;
        select.appendChild(option);
    }
}

// Card controls follow the planning screen's.
function syncFromPlanning() {
    const fuelOn = !!$('showGasStations')?.checked;
    if ($('cardShowFuel')) $('cardShowFuel').checked = fuelOn;
    if ($('cardShowWind')) $('cardShowWind').checked = !!$('showWindIcons')?.checked;
    if ($('cardShowClearances')) $('cardShowClearances').checked = !$('map')?.classList.contains('hide-clearance-markers');
    if ($('cardShowCampgrounds')) $('cardShowCampgrounds').checked = !$('map')?.classList.contains('hide-campground-markers');
    const select = $('cardFuelFilter');
    if (select) {
        const chosen = availableFuelChecks().find((cb) => cb.checked);
        if (chosen) select.value = chosen.value;
        select.disabled = !fuelOn;
    }
}

function setPlanningControl(input, checked) {
    if (!input || input.checked === checked) return;
    input.checked = checked;
    input.dispatchEvent(new Event('change', { bubbles: true }));
}

export function initRouteCardLayers() {
    if (!$('routeCardLayers')) return;
    fillFuelSelect();
    syncFromPlanning();

    $('cardShowFuel').addEventListener('change', (e) => setPlanningControl($('showGasStations'), e.target.checked));
    $('cardShowWind').addEventListener('change', (e) => setPlanningControl($('showWindIcons'), e.target.checked));
    $('cardShowClearances').addEventListener('change', (e) => {
        $('map')?.classList.toggle('hide-clearance-markers', !e.target.checked);
    });
    $('cardShowCampgrounds')?.addEventListener('change', (e) => {
        $('map')?.classList.toggle('hide-campground-markers', !e.target.checked);
    });
    $('cardFuelFilter').addEventListener('change', (e) => {
        // One choice from the card replaces whatever was ticked (the planning
        // screen allows several brands at once). Untick the rest quietly, then
        // tick the choice and fire its change so the markers refresh once.
        const cb = fuelChecks().find((c) => c.value === e.target.value);
        if (!cb) return;
        fuelChecks().forEach((c) => { if (c !== cb) c.checked = false; });
        cb.checked = true;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Planning-screen changes (and the account's market arriving) update the
    // card -- after the planning screen's own handlers, which may untick other
    // fuel options in response.
    const syncSoon = () => setTimeout(syncFromPlanning, 0);
    ['showGasStations', 'showWindIcons'].forEach((id) => $(id)?.addEventListener('change', syncSoon));
    fuelChecks().forEach((cb) => cb.addEventListener('change', syncSoon));
    window.addEventListener('zephyr:marketchange', () => { fillFuelSelect(); syncFromPlanning(); });
}
